/**
 * InvestorPitchPage — the main interactive pitch deck page.
 * Protected by access code, with snap-scroll sections, arrow-key navigation,
 * side navigation, and scroll progress.
 */
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Download, ChevronUp, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import versions, { DEFAULT_VERSION } from "../data/pitchDeckVersions";
import { PitchSettingsProvider } from "../contexts/PitchSettingsContext";
import { UnitEconomicsProvider } from "../contexts/UnitEconomicsContext";
import InvestorAccessGate from "../components/investor/InvestorAccessGate";
import VersionSwitcher from "../components/investor/VersionSwitcher";
import ValphaLogo from "../branding/logo";

// Section components
import HeroSection from "../components/investor/sections/HeroSection";
import ProblemSection from "../components/investor/sections/ProblemSection";
import SolutionSection from "../components/investor/sections/SolutionSection";
import BusinessModelSection from "../components/investor/sections/BusinessModelSection";
import MarketSection from "../components/investor/sections/MarketSection";
import TractionSection from "../components/investor/sections/TractionSection";
import UnitEconomicsSection from "../components/investor/sections/UnitEconomicsSection";
import CompetitiveSection from "../components/investor/sections/CompetitiveSection";
import TeamSection from "../components/investor/sections/TeamSection";
import RoadmapSection from "../components/investor/sections/RoadmapSection";
import RisksSection from "../components/investor/sections/RisksSection";
import AskSection from "../components/investor/sections/AskSection";

// Map section types to components
const sectionComponents = {
  hero: HeroSection,
  problem: ProblemSection,
  solution: SolutionSection,
  businessModel: BusinessModelSection,
  market: MarketSection,
  traction: TractionSection,
  unitEconomics: UnitEconomicsSection,
  competitive: CompetitiveSection,
  team: TeamSection,
  roadmap: RoadmapSection,
  risks: RisksSection,
  ask: AskSection,
};

function InvestorPitchPageInner() {
  const navigate = useNavigate();
  const [versionKey, setVersionKey] = useState(() => {
    return sessionStorage.getItem("pitch_version") || DEFAULT_VERSION;
  });
  const pitchDeck = versions[versionKey]?.data || versions[DEFAULT_VERSION].data;

  // Build ordered list of section ids (including footer)
  const sectionIds = useMemo(
    () => [...pitchDeck.sections.map((s) => s.id), "__footer"],
    [pitchDeck]
  );

  // Derive nav items from current version's sections
  const navItems = useMemo(() =>
    pitchDeck.sections
      .filter((s) => s.label || s.type === "hero")
      .map((s) => ({ id: s.id, label: s.label || "Overview" })),
    [pitchDeck]
  );

  const handleVersionChange = (key) => {
    setVersionKey(key);
    sessionStorage.setItem("pitch_version", key);
    scrollToSection(sectionIds[0]);
  };

  const [hasAccess, setHasAccess] = useState(() => sessionStorage.getItem("investor_access") === "1");
  const [navOpen, setNavOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSection, setActiveSection] = useState("hero");
  const [showScrollTop, setShowScrollTop] = useState(false);
  const containerRef = useRef(null);
  const isScrollingRef = useRef(false);

  // ── Active section tracking via IntersectionObserver ──
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            const id = entry.target.id;
            if (id && id !== "__footer") setActiveSection(id);
          }
        }
      },
      { root: container, threshold: 0.5 }
    );

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sectionIds]);

  // ── Scroll progress tracking ──
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight - container.clientHeight;
      setScrollProgress(scrollHeight > 0 ? scrollTop / scrollHeight : 0);
      setShowScrollTop(scrollTop > 600);
    };
    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  // ── Scroll to a specific section ──
  const scrollToSection = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "instant" });
    setNavOpen(false);
  }, []);

  // ── Arrow key / Page Up/Down navigation ──
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't capture if user is in an input/modal
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) return;

      let direction = 0;
      if (e.key === "ArrowDown" || e.key === "PageDown" || e.key === " ") direction = 1;
      else if (e.key === "ArrowUp" || e.key === "PageUp") direction = -1;
      else if (e.key === "Home") { e.preventDefault(); scrollToSection(sectionIds[0]); return; }
      else if (e.key === "End") { e.preventDefault(); scrollToSection(sectionIds[sectionIds.length - 1]); return; }
      else return;

      e.preventDefault();
      if (isScrollingRef.current) return;

      const currentIdx = sectionIds.indexOf(activeSection);
      const nextIdx = Math.max(0, Math.min(sectionIds.length - 1, currentIdx + direction));
      if (nextIdx !== currentIdx) {
        isScrollingRef.current = true;
        scrollToSection(sectionIds[nextIdx]);
        setTimeout(() => { isScrollingRef.current = false; }, 400);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeSection, sectionIds, scrollToSection]);

  if (!hasAccess) {
    return (
      <InvestorAccessGate
        correctCode={pitchDeck.meta.accessCode}
        onGranted={() => setHasAccess(true)}
      />
    );
  }

  return (
    <div className="relative bg-[#0A0F1A] text-white h-screen font-inter overflow-hidden">
      {/* ── Progress bar ── */}
      <div className="fixed top-0 left-0 right-0 z-50 h-0.5">
        <motion.div
          className="h-full bg-gradient-to-r from-[#3B82F6] to-[#4FA9A1]"
          style={{ width: `${scrollProgress * 100}%` }}
        />
      </div>

      {/* ── Top bar ── */}
      <header className="fixed top-0 left-0 right-0 z-40 bg-[#0A0F1A]/80 backdrop-blur-lg border-b border-white/5" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-2.5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setNavOpen(!navOpen)}
              className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors xl:hidden"
            >
              {navOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
            <img src={ValphaLogo} alt="Valpha" className="h-6 opacity-80" />
            <span className="text-[10px] text-gray-400 uppercase tracking-widest hidden sm:inline">
              Investor Presentation
            </span>
          </div>

          <div className="flex items-center gap-3">
            <VersionSwitcher current={versionKey} onChange={handleVersionChange} />
            <button
              onClick={() => navigate("/invest/admin")}
              className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
              title="Visual settings"
            >
              <Settings className="w-4 h-4 text-gray-400" />
            </button>
            <span className="text-[10px] text-gray-400 hidden sm:inline">
              Confidential
            </span>
          </div>
        </div>
      </header>



      {/* ── Mobile navigation drawer ── */}
      <AnimatePresence>
        {navOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setNavOpen(false)}
              className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm xl:hidden"
            />
            <motion.nav
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 bottom-0 z-50 w-64 bg-[#0F1826] border-r border-white/10 pt-16 px-4 xl:hidden overflow-y-auto"
            >
              <div className="space-y-1">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`block w-full text-left text-sm py-2.5 px-3 rounded-xl transition-all ${
                      activeSection === item.id
                        ? "text-white bg-white/10 font-medium"
                        : "text-gray-400 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>

      {/* ── Body layout (below fixed header) ── */}
      <div
        className="absolute left-0 right-0 bottom-0 flex overflow-hidden"
        style={{ top: 'calc(52px + env(safe-area-inset-top, 0px))' }}
      >
        {/* ── Desktop side navigation (in flow, not fixed) ── */}
        <nav className="hidden xl:flex flex-col w-56 shrink-0 justify-center pl-4 overflow-y-auto bg-[#0A0F1A]">
          <div className="space-y-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`block w-full text-left text-sm py-2 px-3 rounded-lg transition-all duration-200 ${
                  activeSection === item.id
                    ? "text-white bg-white/10 font-medium"
                    : "text-gray-400 hover:text-gray-200 hover:bg-white/5"
                }`}
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`w-1.5 h-1.5 rounded-full transition-all ${
                      activeSection === item.id ? "bg-[#3B82F6]" : "bg-gray-700"
                    }`}
                  />
                  {item.label}
                </span>
              </button>
            ))}
          </div>
        </nav>

        {/* ── Snap-scroll sections container ── */}
        <main
          ref={containerRef}
          className="flex-1 min-w-0 h-full overflow-y-auto overflow-x-hidden snap-y snap-proximity md:snap-mandatory"
          style={{ scrollBehavior: "auto" }}
        >
        {pitchDeck.sections.map((section) => {
          const Component = sectionComponents[section.type];
          if (!Component) return null;
          return <Component key={section.id} data={section} />;
        })}

        {/* Footer — snaps like a section */}
        <footer
          id="__footer"
          className="min-h-full snap-start snap-always bg-[#070B14] border-t border-white/5 flex flex-col items-center justify-center text-center px-4"
        >
          <img src={ValphaLogo} alt="Valpha" className="h-8 mx-auto mb-4 opacity-60" />
          <p className="text-gray-400 text-sm mb-2">Thank you for your time</p>
          <p className="text-gray-400 text-xs">
            Confidential & proprietary — not for redistribution
          </p>
          <p className="text-gray-500 text-[10px] mt-4">
            © {new Date().getFullYear()} Valpha.io — All rights reserved
          </p>
        </footer>
        </main>
      </div>

      {/* ── Scroll to top button ── */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            onClick={() => scrollToSection(sectionIds[0])}
            className="fixed bottom-6 right-6 z-40 w-10 h-10 rounded-full bg-[#3B82F6]/20 border border-[#3B82F6]/30 flex items-center justify-center hover:bg-[#3B82F6]/30 transition-colors backdrop-blur-sm"
          >
            <ChevronUp className="w-5 h-5 text-[#60A5FA]" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Wrapper that provides settings + unit economics contexts. */
export default function InvestorPitchPage() {
  return (
    <PitchSettingsProvider>
      <UnitEconomicsProvider>
        <InvestorPitchPageInner />
      </UnitEconomicsProvider>
    </PitchSettingsProvider>
  );
}
